import type { QuestKey } from "@/components/QuestsV2/types";
import { appConfigFn } from "@/constants/appConfig";
import { EnvironmentNames } from "@/types";
import { onDarkBase } from "@biom3/design-tokens";
import { mediaQuerySizesHelper } from "@biom3/react";
import * as checkout from "@imtbl/checkout-sdk";
import { BREAKPOINTS } from "@ui-kit/hooks/screenSizeHooks";
import { Roboto, Roboto_Mono } from "next/font/google";
import localFont from "next/font/local";

export const APP_HEADER_HEIGHT = "94px";
export const SIDEBAR_WIDTH = "280px";
export const HOME_PAGE = "/";
export const QUEST_PAGE = "/quests";
export const ONBOARDING_PAGE = "/onboarding";
export const REFERRAL_PAGE = "/referral";
export const MODAL_WIDTH = "430px";
export const MODAL_HEIGHT = "660px";
export const HERO_GRID_MIN_COLUMN_WIDTH = "330px";

export const NEXT_IMAGE_QUALITY = 85;

export const appConfig = appConfigFn();
// @NOTE: use this when we have no valid image url to pass to Biome Image
// components, as this will have them display default imagery
export const BAD_IMAGE_URL = "https://something";
export const PINATA_GATEWAY_URL = "https://gateway.pinata.cloud";

export const TOOLKIT_URL =
  appConfig.ENVIRONMENT === "production"
    ? "https://toolkit.immutable.com"
    : "https://checkout-playground.sandbox.immutable.com";

export const chainSlug =
  appConfig.ENVIRONMENT === EnvironmentNames.PRODUCTION
    ? "imtbl-zkevm-mainnet"
    : appConfig.ENVIRONMENT === EnvironmentNames.SANDBOX
      ? "imtbl-zkevm-testnet"
      : "imtbl-zkevm-devnet";

export const bodyPrimary = Roboto({
  weight: ["400", "500", "700"],
  style: ["normal", "italic"],
  subsets: ["latin"],
  display: "swap",
});

export const bodySecondary = Roboto_Mono({
  weight: ["400", "700"],
  style: ["normal", "italic"],
  subsets: ["latin"],
  display: "swap",
});

export const headingPrimary = localFont({
  display: "swap",
  src: [
    {
      path: "../../node_modules/@biom3/design-tokens/fonts/suisseintl-regular.woff2",
      weight: "400",
      style: "normal",
    },
    {
      path: "../../node_modules/@biom3/design-tokens/fonts/suisseintl-medium.woff2",
      weight: "600",
      style: "normal",
    },
    {
      path: "../../node_modules/@biom3/design-tokens/fonts/suisseintl-bold.woff2",
      weight: "700",
      style: "normal",
    },
  ],
});

export const CUSTOM_BIOME_THEME = {
  base: {
    ...onDarkBase,
    font: {
      ...onDarkBase.font,
      family: {
        heading: {
          primary: headingPrimary.style.fontFamily,
          secondary: headingPrimary.style.fontFamily,
        },
        body: {
          primary: bodyPrimary.style.fontFamily,
          secondary: bodySecondary.style.fontFamily,
        },
      },
    },
    breakpoint: BREAKPOINTS,
  },
  globalConfig: {
    imageResizeServiceUrl: appConfig.IMAGE_RESIZER_URL,
  },
} as const;

export const QUEST_CAROUSEL_IMAGE_SIZES = mediaQuerySizesHelper([
  {
    rule: "default",
    width: "calc(100dvw - 48px - 16px)",
    // 48px: 24px padding on each side inner padding
    // 16px: 8px padding on each side outer padding
  },
  {
    rule: "minWidth",
    breakpoint: "medium",
    width: `calc(100dvw - ${SIDEBAR_WIDTH} - 48px - 32px)`,
    // 220px: sidebar width,
    // 48px: 24px padding on each side inner padding
    // 32px: 16px padding on each side outer padding
  },
  {
    rule: "minWidth",
    breakpoint: "xLarge",
    width: `calc((100dvw - ${SIDEBAR_WIDTH} - 48px - 32px) * 0.72)`,
    // 220px: sidebar width,
    // 48px: 24px padding on each side inner padding
    // 32px: 16px padding on each side outer padding
    // 0.72: the % width of the main carousel display area
  },
])({
  // For the purposes of consumption by mediaQuerySizesHelper,
  // we dont need the calculcated breakpointAsArray field inside the theme
  base: { ...CUSTOM_BIOME_THEME.base, breakpointAsArray: [] },
});

export const APP_GLOBAL_SX = {
  "html, body": {
    p: "0",
    m: "0",
    bg: "base.color.neutral.1000",
    h: "100dvh",
  },

  "#__next": {
    w: "100%",
    h: "100%",
  },
} as const;

export const SOCIAL_LINK_ERROR_MESSAGES = {
  GENERIC: "That didn't work. Go back to the other tab and try again.",
  GENERIC_MODAL_ERROR: "Something went wrong, please try again later.",
  SOCIAL_ACCOUNT_ALREADY_LINKED:
    "This social account is already linked to another Passport user",
  DIFFERENT_SOCIAL_ACCOUNT_ALREADY_LINKED:
    "You already have a different social account linked",
  NOT_LOGGED_IN: "User is not authenticated yet",
  X_FOLLOW_QUEST_UNAVAILABLE:
    "X Follow Quest is temporarily unavailable. Please try again later.",
  RATE_LIMIT_ERROR: "API rate limit exceeded",
  X_DUPLICATE_POST_CONTENT:
    "You are not allowed to create a Tweet with duplicate content.",
};

export const CLAIM_MODAL_PARAMS: Record<
  string,
  { title: string; caption: string }
> = {
  "crossover-fates": {
    title: "Progress towards a chance to win a Nintendo Switch 2!",
    caption:
      'Click "Claim" once you have completed all quests on the Might & Magic Fates leaderboard at least one time. Claim all rewards across Might & Magic Fates, Meta Toy Dragonz Saga and Triumph Games to go into the draw to win a Nintendo Switch 2 console!',
  },
  "crossover-meta-toy": {
    title: "Progress towards a chance to win a Nintendo Switch 2!",
    caption:
      'Click "Claim" once you have completed all quests on the Meta Toy Dragonz Saga leaderboard at least one time. Claim all rewards across Might & Magic Fates, Meta Toy Dragonz Saga and Triumph Games to go into the draw to win a Nintendo Switch 2 console!',
  },
  "crossover-triumph": {
    title: "Progress towards a chance to win a Nintendo Switch 2!",
    caption:
      'Click "Claim" once you have registered your score on the Triumph Games Creator Leaderboard. Claim all rewards across Might & Magic Fates, Meta Toy Dragonz Saga and Triumph Games to go into the draw to win a Nintendo Switch 2 console!',
  },
};

export interface SocialConnection {
  platform: "X" | "Discord" | "Telegram" | "Steam";
  key: QuestKey;
  questId: string;
}

export const SOCIAL_CONNECTIONS: SocialConnection[] = [
  { platform: "X", key: "x-v2", questId: "link-x-account" },
  { platform: "Discord", key: "discord-v2", questId: "link-discord-account" },
  {
    platform: "Telegram",
    key: "telegram-v2",
    questId: "link-telegram-account",
  },
  { platform: "Steam", key: "steam", questId: "link-steam-account" },
];

export const IMMUTABLE_GAME_NAME = "Immutable";
export const IMMUTABLE_GAME_ID = "11111111-1111-1111-1111-111111111111";
export const ONBOARDING_QUEST_ID = `${IMMUTABLE_GAME_ID}-onboarding-event`;

export const MAIN_QUESTS_GROUP = "Main Quests";
export const SIDE_QUESTS_GROUP = "Side Quests";

export enum OnboardingExperience {
  CHESS_UNIVERSE = "chessuniverse",
  GENERAL_ONBOARDING = "generalonboarding",
  REFERRAL = "referral",
}

export const SESSION_ACTIVITY_IDS = new Map<string, string>([
  // Game ID from retool - client ID from 'Gamer Profile' hub project
  ["f8dc910f-6fdb-4b16-8d3e-d2586b5d3b2b", "8nlSxeQWJ4ruwZa6odVdE2h3USskPELA"], // RavenQuest
  ["e38dffc2-2d44-4b26-8e2f-c593bddececc", "cNec3A3E7wlrEZjuCvxEsVGEWHQTVrpA"], // Overtake
  ["0c941516-d4df-4adf-aa42-fe601ee852cf", "Hu7v4LLDnt3sFInGgbNAUbUaMxSuxKTJ"], // Norma in Metaland
  ["96191677-0a86-4718-a863-d8e7818fc063", "IGOFNYSgfP5CmfYgRXUz2B9grchyL3Zw"], // Immortal Rising 2
  ["e2ac7c08-890d-4062-a59e-8e5737e913ea", "ejM4TSvOw9fTUXWfi9rAX0CRHrH8y2cJ"], // Swords of Blood
  ["f92365fb-4b6b-43f8-be7b-f28a9d3df2d1", "T9TO7vrErJcsrhAFftAmfaerh0M2ObLY"], // Champz
  ["6253fded-0d07-4e41-8239-e935f6f5748e", "aRBPWYcxq7nVhJMpxnSiVHDy3RXa98BR"], // Treeverse
  ["bc7cfbe8-cf31-4dc3-b202-3adbf0873dd0", "9Eq8zx8xjdML2ipkzzcOIbXyufS7vEem"], // ZipClash
  ["480fa2f5-635a-4d94-9e92-58b6552347c1", "2dCBv3R9AVjHJcTRBGpOAIA1mZh1wmoR"], // Echoes of Cryptalia
  ["4f188ae5-8439-40dd-b6b7-c4626c7badec", "9aZufbI7Q8MRp5CPHIHZR16BWwam2Wbh"], // Baby Shark Universe
  ["d99f87e1-9b4e-4e40-ad64-e319a0d79335", "UuIBvzXrlImVheKomZaaCW3hCbQVf93Q"], // Tokyo Beast
  ["75521950-3d5f-4bdd-883f-ba792c0f4c06", "Gu292g4TsCIbiMBoHbKUbh7j7E6wekGY"], // Somnis Rumble Rush
  ["ccedb5c9-180d-4b21-b480-9bc264dd5912", "WA06nNeBI5aL6iAiXzzkMX7gwXWswc6m"], // Bitpool
  ["01ed155f-637e-4940-9ff8-d9d9519d6d75", "f8dDbHASXz5EZZDI7m0LeNHNYK7J9R0y"], // Battle of Souls
  ["f8e26689-9487-463d-af6c-48d7bae6f5fe", "H3SqnaWHiz3It9xK9X7FNYkzi4b7WuFx"], // Self-made Billionaire
  ["ba82b246-e4f8-4515-b3b8-5fab8a42ec60", "Tqwra5zwmJcwIhue5jrtaWPcjBFaaTrT"], // Galaxy of Lemuria
  ["c6c92500-1667-4318-a925-a3a8d9c7d3af", "90XQcQY9Gp7BsVToY8G4if3nfOJJDiB7"], // IN Match3
  ["233a023c-b8ff-4cd6-b1eb-fc4986e66864", "ctqzXDbdLGXUH5pyZpgkoMZUWbOPTz5U"], // Overknights
  ["607888e1-133e-40ce-b5c6-e00be24c5494", "H1kSL49oVQXTtIaCUBr89qNwNAsxeTsf"], // Chumbi Valley
  ["b6cc4d07-31af-4702-b011-ff231d738b4e", "ygSOB7aJUdfo3mCwb7hJbZV0TmTCmXhu"], // Battle Rise
  ["0dc1aad9-cd80-4a59-809a-6af7d1255d2b", "FKs2CRk4WprZxGcnaePtubvb1YN2hs3U"], // Supernova Shards
  ["261fdf5e-2511-4381-aa3b-945de396b153", "lEmWgTefvW8sSqMm0HUIj4J7f0MRTWbO"], // Syndicate of Vigilantes
  ["ecd70429-fec4-4a00-a0dd-9c236c2cea69", "gFYsfup7W87DxmIpnGSfPJVD8ChU10XZ"], // Hellmaster
  ["c07cb827-4590-4d14-b5d6-b47e1b34e74d", "pYudCOhj5w0i4ECb66pIbnKGc4C9184C"], // Legends of Elumia
  ["f113cf04-4a98-4365-9622-6916ad30890e", "eM1sX3ySi0cVgjsTEv7CtfubhUcC4sMO"], // Mercenary Children
  ["8d166491-1507-47e3-9663-477fffa50a18", "jLR68MUgaFr8aBNcmnFISy7NfzBHvWxT"], // Freya: The Starfall
  ["2edf9422-28f2-4402-a588-49860b61d755", "qhrUX8UN5Wo19IoxuTDkI5PHHlcLEgbm"], // Eternal League
  ["1186b77c-7131-4cf4-8840-2e92d1df3bb3", "4Bjknsl4v1sNSA18GmsxsCvueg62DbUM"], // ArmourX
  ["f6f6725a-8c1e-4cf3-8a88-bb88804f894e", "7R6Euu4EQ1WmkZCT1m7svlXRTqiesU4L"], // Dice Go
  ["da200b52-f706-433d-8617-03c35d02cdfe", "qo9Bo9oejhTE6aIkwJ1tfOp3ex95Nla3"], // Meta Toy DragonZ SAGA
  ["5bb4e17b-9de4-4ebc-b164-f3aacbb57d66", "ZabAMafQRA9bWE1XatAtBYNe6DRamgqE"], // RoRo Birds
  ["9fe4bf9c-3aeb-41e9-bd9a-57539a6116d5", "SUWHo0BENmRvZqF7xU6KX46oJzXQEJPq"], // Might and Magic Fates
  ["91a264a7-dfd9-4338-a338-18326908a6a4", "aLuJy8oj1UhBFFRVSLmaRK61oismzxrR"], // Nomstead Kingdom So
  ["8c6b16c3-3dd1-40e5-ba25-a7681666d3cc", "ISKhH6ZKD2Z037zcnGf2qY30PGOPWhQo"], // Bossfighters
  ["30f6b871-4bac-4b9d-aabf-4a3d11294cf9", "vDxGYiBhD6hQ137EYW9AyORz33kxzzUD"], // Chainers
  ["65e93b9e-e054-4f3a-aed8-bb5d210b0302", "fTvsyXm1ksWba4XQKHBRWEcT0NrT9z9x"], // Slime Miner
  ["b0c04075-978f-46ef-b6a5-63faec33275e", "X6DoVvyi2PiRjp9WXHctmPM4uOewq8nb"], // R.O.H.A.N.2 Global
  ["925b48b9-fb2f-4bb0-9054-700eb9cf51eb", "ruhlOIlcQUWhUmUYEglbVxeIHJ4KQRbZ"], // 8 Ball
  ["f8cd331f-cc7f-40ea-aedb-e45deb9a2d2a", "0NB3zlcqWJMIvf6ogz8fHzNha3RGYqrl"], // Basejump
  ["6a18cbdc-d9e5-400c-85c5-8cc5cedc28ad", "2EJeCcjrgFDhSirGQrzjzwhOXf48Lp9W"], // War of Art
  ["2a91e89f-c41f-4758-89a9-f8d52310cc93", "eUuVSHk2VmqEmkheruUiqaVn0F1bJF7i"], // RavenIdle
  ["95237c25-ded2-43f9-ab8d-26ca10f2414b", "ThCHbKb2XWrlxsuMvohZQUKP8lARLPFE"], // Worldon
  ["450ff0b8-d6cd-4310-888b-65af5185788f", "RJRwJbecdnQPM6buGqD585LEllDJjN4p"], // Daemons
  ["fb9f108a-7f6b-4472-90ed-8bc36f97049c", "3S9EY9fxdlf77zdamsxwhvr3fW3V9Cmv"], // Engines of Fury
  ["5bbcb20b-1295-4888-b64c-9bac69972a76", "BjlLRpcBp168NVOXh5A437OeB5RReiA9"], // Warp Gaming
  ["a36f8c39-3fc2-4a71-8044-7af0637bc782", "PSWpBJy4M2HPj8VSBUlvLUoNTYLR4lkS"], // Wilder Worlds
  ["5cc64801-ec4b-44de-a2cc-4d18df4775db", "yWrAknm7V4X8XG8YHcs7QNeWzdf3S6UF"], // Cartoon Craft
  ["1a1aec67-b340-4601-9e79-59a724fafc35", "GbqScpoJ86vbd1pxOGf1ovxyCRkQ1Oit"], // Villains
]);

// This is for continuous engagement tracking
export const SESSION_ACTIVITY_CONTRACT_ADDRESSES = new Map<string, string>([
  [
    "b6cc4d07-31af-4702-b011-ff231d738b4e",
    "0xf204D5697C128CfD1fF6B0325C8bf5C236B2F450",
  ], // BattleRise
]);

const ZKEVM_NATIVE_TOKEN = {
  name: "IMX",
  symbol: "IMX",
  decimals: 18,
  address: "native",
};

export const DEV_CHAIN_ID_NETWORK_MAP = new Map([
  [
    checkout.ChainId.SEPOLIA,
    {
      chainIdHex: `0x${checkout.ChainId.SEPOLIA.toString(16)}`,
      chainName: checkout.ChainName.SEPOLIA,
      rpcUrls: ["https://checkout-api.dev.immutable.com/v1/rpc/eth-sepolia"],
      nativeCurrency: {
        name: "Sep Eth",
        symbol: "ETH",
        decimals: 18,
      },
      blockExplorerUrls: ["https://sepolia.etherscan.io/"],
    },
  ],
  [
    checkout.ChainId.IMTBL_ZKEVM_DEVNET,
    {
      chainIdHex: `0x${checkout.ChainId.IMTBL_ZKEVM_DEVNET.toString(16)}`,
      chainName: checkout.ChainName.IMTBL_ZKEVM_DEVNET,
      rpcUrls: ["https://rpc.dev.immutable.com"],
      nativeCurrency: ZKEVM_NATIVE_TOKEN,
    },
  ],
]);

export const STEAM_SETTINGS_URL = "https://steamcommunity.com/my/edit/settings";

export const KEY_SPLINE_URL = `${appConfig.SPLINE_VIEWER}/T5erQ-DfxB5M7VSg?1d5c8b83-2300-4c3c-8e58-56b830fd6c1c`;

// TODO: Clean up after promotion
export const PROMOTIONAL_WALLETS = [
  // Kols
  "0xa1406b4206330df8ea20b5334cf21a32992468a1",
  "0xab70767614f069afe0891a99bb05e330dc885d01",
  // Test
  "0x646bb0221ac0a92759a771e7fa8c661306116728", // prod test
  "0x89ed524f95c591d1d43a5786b5d9eccad879788f", // dev test
];

export const PROMOTIONAL_PRIZES_STORAGE_KEY = "promotional_prizes_won";
